import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Users, TrendingUp, AlertTriangle, Clock, BarChart3,
  Search, Filter, ChevronDown, ChevronLeft, Flame, Target,
  BookOpen, CheckCircle, XCircle, Trophy, Calendar, Download,
  RefreshCw, Loader2, Building2, UserCheck, AlertCircle, Settings,
  Link2, Unlink, ExternalLink, Eye, EyeOff, Save, Zap, Upload, X, Heart,
  Video, Play, Trash2, Plus, Smartphone, Palette, Mail
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { OposaiLogo } from '@/components/TuPlazaIALogo';
import PWABrandingConfig from '@/components/institutional/PWABrandingConfig';
import InstitutionalCRM from '@/components/institutional/InstitutionalCRM';
import LanguageSelector from '@/components/LanguageSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InstitutionalDashboard = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRisk, setFilterRisk] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [institution, setInstitution] = useState(null);
  
  // Import CSV State
  const [showImportModal, setShowImportModal] = useState(false);
  const [importingCsv, setImportingCsv] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Add Student Manual State
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [addingStudent, setAddingStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({
    name: '',
    email: '',
    profession_id: ''
  });
  
  // CRM Integration State
  const [activeTab, setActiveTab] = useState('students'); // students, zoom, crm
  const [crmProviders, setCrmProviders] = useState([]);
  const [crmConnection, setCrmConnection] = useState(null);
  const [savingCrm, setSavingCrm] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  
  // Zoom State
  const [zoomConfigured, setZoomConfigured] = useState(false);
  const [zoomMeetings, setZoomMeetings] = useState([]);
  const [showZoomConfig, setShowZoomConfig] = useState(false);
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  
  // Email Config State
  const [emailConfig, setEmailConfig] = useState({
    mode: 'tuplazaia', // 'tuplazaia' o 'custom'
    aws_access_key_id: '',
    aws_secret_access_key: '',
    aws_region: 'eu-west-1',
    ses_from_email: '',
    ses_from_name: ''
  });
  const [savingEmailConfig, setSavingEmailConfig] = useState(false);
  const [showAwsSecret, setShowAwsSecret] = useState(false);
  const [savingZoom, setSavingZoom] = useState(false);
  const [zoomForm, setZoomForm] = useState({
    account_id: '',
    client_id: '',
    client_secret: ''
  });
  const [meetingForm, setMeetingForm] = useState({
    topic: '',
    description: '',
    start_time: '',
    duration_minutes: 60
  });
  
  const [crmForm, setCrmForm] = useState({
    crm_type: '',
    api_key: '',
    api_secret: '',
    domain: '',
    webhook_url: '',
    location_id: '',
    sync_students: true,
    sync_progress: false,
    sync_frequency: 'daily'
  });

  useEffect(() => {
    loadData();
    loadCrmProviders();
    loadCrmConnection();
  }, []);

  useEffect(() => {
    if (institution?.institution_id) {
      loadZoomData();
    }
  }, [institution]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [studentsRes, statsRes, institutionRes] = await Promise.all([
        axios.get(`${API}/institutional/students`, { headers }).catch(() => ({ data: { students: [] } })),
        axios.get(`${API}/institutional/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/institutional/info`, { headers }).catch(() => ({ data: null }))
      ]);

      setStudents(studentsRes.data.students || []);
      setStats(statsRes.data);
      setInstitution(institutionRes.data);
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const loadZoomData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Check zoom config status
      if (institution?.institution_id) {
        const configRes = await axios.get(
          `${API}/zoom/config/institution/${institution.institution_id}`,
          { headers }
        ).catch(() => ({ data: { configured: false } }));
        setZoomConfigured(configRes.data.configured);
      }
      
      // Load meetings
      const meetingsRes = await axios.get(
        `${API}/zoom/meetings?institution_id=${institution?.institution_id || ''}`,
        { headers }
      ).catch(() => ({ data: { meetings: [] } }));
      setZoomMeetings(meetingsRes.data.meetings || []);
    } catch (error) {
      console.error('Error loading zoom data:', error);
    }
  };

  const handleConfigureZoom = async () => {
    if (!zoomForm.account_id || !zoomForm.client_id || !zoomForm.client_secret) {
      toast.error('Completa todos los campos');
      return;
    }
    
    setSavingZoom(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(
        `${API}/zoom/config/institution/${institution.institution_id}`,
        zoomForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Zoom configurado correctamente');
      setShowZoomConfig(false);
      setZoomConfigured(true);
      setZoomForm({ account_id: '', client_id: '', client_secret: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al configurar Zoom');
    } finally {
      setSavingZoom(false);
    }
  };

  const handleCreateMeeting = async () => {
    if (!meetingForm.topic || !meetingForm.start_time) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    
    setSavingZoom(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(
        `${API}/zoom/meetings`,
        {
          ...meetingForm,
          profession_id: institution?.profession_id || 'general',
          institution_id: institution?.institution_id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Clase creada correctamente');
      setShowCreateMeeting(false);
      setMeetingForm({ topic: '', description: '', start_time: '', duration_minutes: 60 });
      loadZoomData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear clase');
    } finally {
      setSavingZoom(false);
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('¿Cancelar esta clase?')) return;
    
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(
        `${API}/zoom/meetings/${meetingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Clase cancelada');
      loadZoomData();
    } catch (error) {
      toast.error('Error al cancelar clase');
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const loadCrmProviders = async () => {
    try {
      const res = await axios.get(`${API}/crm/providers`);
      setCrmProviders(res.data.providers || []);
    } catch (error) {
      console.error('Error loading CRM providers:', error);
    }
  };

  const loadCrmConnection = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/crm/connection`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.connection) {
        setCrmConnection(res.data.connection);
        setCrmForm(prev => ({
          ...prev,
          ...res.data.connection
        }));
      }
    } catch (error) {
      // No connection exists yet - that's OK
    }
  };

  const saveCrmConnection = async () => {
    if (!crmForm.crm_type) {
      toast.error('Selecciona un CRM');
      return;
    }

    setSavingCrm(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/crm/connection`, crmForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Conexión CRM guardada correctamente');
      loadCrmConnection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando conexión CRM');
    } finally {
      setSavingCrm(false);
    }
  };

  const disconnectCrm = async () => {
    if (!confirm('¿Estás seguro de desconectar el CRM?')) return;
    
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/crm/connection`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCrmConnection(null);
      setCrmForm({
        crm_type: '',
        api_key: '',
        api_secret: '',
        domain: '',
        webhook_url: '',
        location_id: '',
        sync_students: true,
        sync_progress: false,
        sync_frequency: 'daily'
      });
      toast.success('CRM desconectado');
    } catch (error) {
      toast.error('Error desconectando CRM');
    }
  };

  const loadStudentDetails = async (studentId) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/institutional/student/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudentDetails(res.data);
    } catch (error) {
      toast.error('Error cargando detalles');
    }
  };

  const exportStudentsCsv = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/institutional/export/metrics`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `alumnos_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Archivo CSV descargado');
    } catch (error) {
      toast.error('Error exportando datos');
    }
  };

  const handleImportCsv = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Por favor selecciona un archivo CSV');
      return;
    }
    
    setImportingCsv(true);
    setImportResult(null);
    
    try {
      const token = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/institutional/students/bulk-upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      
      if (response.data.added > 0) {
        toast.success(`${response.data.added} alumnos importados correctamente`);
        loadData(); // Reload students
      }
      
      if (response.data.failed?.length > 0) {
        toast.warning(`${response.data.failed.length} alumnos no pudieron importarse`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error importando archivo');
    } finally {
      setImportingCsv(false);
      event.target.value = ''; // Reset file input
    }
  };

  // Add single student manually
  const handleAddStudent = async () => {
    if (!newStudent.name || !newStudent.email) {
      toast.error('Nombre y email son obligatorios');
      return;
    }
    
    setAddingStudent(true);
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/institutional/students/add`, {
        name: newStudent.name,
        email: newStudent.email,
        profession_id: newStudent.profession_id || null,
        send_welcome_email: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Alumno ${newStudent.name} añadido. Se enviará email con credenciales.`);
      setShowAddStudentModal(false);
      setNewStudent({ name: '', email: '', profession_id: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error añadiendo alumno');
    } finally {
      setAddingStudent(false);
    }
  };

  const filteredStudents = students.filter(s => {
    const matchesSearch = !searchTerm || 
      s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRisk = filterRisk === 'all' || 
      (filterRisk === 'high_risk' && (s.risk_score || 0) > 70) ||
      (filterRisk === 'at_risk' && (s.risk_score || 0) > 40 && (s.risk_score || 0) <= 70) ||
      (filterRisk === 'on_track' && (s.risk_score || 0) <= 40);
    
    return matchesSearch && matchesRisk;
  });

  const getRiskColor = (score) => {
    if (score > 70) return 'text-red-600 bg-red-100';
    if (score > 40) return 'text-amber-600 bg-amber-100';
    return 'text-green-600 bg-green-100';
  };

  const getRiskLabel = (score) => {
    if (score > 70) return 'Alto riesgo';
    if (score > 40) return 'En riesgo';
    return 'En buen camino';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* Header */}
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-slate-100">
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <OposaiLogo size={40} />
              <div>
                <h1 className="font-semibold text-slate-800">Dashboard Institucional</h1>
                <p className="text-sm text-slate-500">{institution?.name || 'Tu institución'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Tab buttons */}
              <div className="flex bg-slate-100 rounded-lg p-1 mr-4">
                <button
                  onClick={() => setActiveTab('students')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'students' 
                      ? 'bg-white text-slate-800 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <Users className="w-4 h-4 inline mr-2" />
                  Alumnos
                </button>
                <button
                  onClick={() => setActiveTab('leads')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'leads' 
                      ? 'bg-white text-slate-800 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <Target className="w-4 h-4 inline mr-2" />
                  Leads/CRM
                </button>
                <button
                  onClick={() => setActiveTab('zoom')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'zoom' 
                      ? 'bg-white text-slate-800 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <Video className="w-4 h-4 inline mr-2" />
                  Clases en Vivo
                </button>
                <button
                  onClick={() => setActiveTab('crm')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'crm' 
                      ? 'bg-white text-slate-800 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <Link2 className="w-4 h-4 inline mr-2" />
                  Integraciones
                </button>
                <button
                  onClick={() => setActiveTab('pwa')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'pwa' 
                      ? 'bg-white text-slate-800 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <Smartphone className="w-4 h-4 inline mr-2" />
                  PWA / Branding
                </button>
                <button
                  onClick={() => setActiveTab('emails')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'emails' 
                      ? 'bg-white text-slate-800 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <Mail className="w-4 h-4 inline mr-2" />
                  Emails
                </button>
              </div>
              <LanguageSelector variant="outline" showFlag={true} showName={false} />
              <Button variant="outline" onClick={loadData}>
                <RefreshCw className="w-4 h-4 mr-2" /> {t('common.refresh', 'Actualizar')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Leads/CRM Tab */}
        {activeTab === 'leads' && (
          <InstitutionalCRM institutionId={institution?.institution_id} />
        )}

        {/* Zoom Tab */}
        {activeTab === 'zoom' && (
          <div className="space-y-6">
            {/* Zoom Status Banner */}
            {zoomConfigured ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-800">Zoom Configurado</p>
                    <p className="text-sm text-green-600">
                      Puedes crear clases en vivo para tus alumnos
                    </p>
                  </div>
                </div>
                <Button onClick={() => setShowCreateMeeting(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Clase
                </Button>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800">Zoom No Configurado</p>
                    <p className="text-sm text-amber-600">
                      Configura tus credenciales de Zoom para crear clases en vivo
                    </p>
                  </div>
                </div>
                <Button onClick={() => setShowZoomConfig(true)}>
                  <Settings className="w-4 h-4 mr-2" />
                  Configurar Zoom
                </Button>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h3 className="font-medium text-blue-900 mb-2">📹 Cómo funcionan las Clases en Vivo</h3>
              <p className="text-sm text-blue-700 mb-3">
                Crea clases de Zoom para que tus alumnos puedan seguir las lecciones en directo.
                Las clases aparecerán en el dashboard de tus estudiantes.
              </p>
              <p className="text-sm text-blue-600">
                <strong>Para configurar Zoom:</strong> Necesitas crear una app Server-to-Server OAuth en{' '}
                <a href="https://marketplace.zoom.us" target="_blank" rel="noopener noreferrer" className="underline">
                  marketplace.zoom.us
                </a>
              </p>
            </div>

            {/* Meetings List */}
            <div className="bg-white rounded-xl border">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold text-lg">Clases Programadas</h3>
                {zoomConfigured && (
                  <Button size="sm" onClick={() => setShowCreateMeeting(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Clase
                  </Button>
                )}
              </div>
              
              {zoomMeetings.length === 0 ? (
                <div className="p-8 text-center">
                  <Video className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No hay clases programadas</p>
                  {zoomConfigured && (
                    <Button className="mt-4" variant="outline" onClick={() => setShowCreateMeeting(true)}>
                      Crear primera clase
                    </Button>
                  )}
                </div>
              ) : (
                <div className="divide-y">
                  {zoomMeetings.map(meeting => (
                    <div key={meeting.meeting_id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{meeting.topic}</h4>
                            {meeting.status === 'scheduled' && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                                Programada
                              </span>
                            )}
                            {meeting.status === 'cancelled' && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">
                                Cancelada
                              </span>
                            )}
                          </div>
                          {meeting.description && (
                            <p className="text-sm text-slate-600 mb-2">{meeting.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDateTime(meeting.start_time)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {meeting.duration_minutes} min
                            </span>
                            {meeting.attendees?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {meeting.attendees.length} asistentes
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {meeting.status === 'scheduled' && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => window.open(meeting.start_url || meeting.join_url, '_blank')}
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Iniciar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(meeting.join_url);
                                toast.success('Enlace copiado');
                              }}
                            >
                              <Link2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteMeeting(meeting.meeting_id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Zoom Config Modal */}
        {showZoomConfig && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4">
              <h3 className="text-xl font-bold mb-4">Configurar Zoom</h3>
              
              <div className="bg-slate-50 rounded-lg p-4 mb-4 text-sm">
                <p className="font-medium mb-2">Cómo obtener las credenciales:</p>
                <ol className="list-decimal list-inside space-y-1 text-slate-600">
                  <li>Ve a <a href="https://marketplace.zoom.us" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">marketplace.zoom.us</a></li>
                  <li>Crea una app de tipo &quot;Server-to-Server OAuth&quot;</li>
                  <li>Copia el Account ID, Client ID y Client Secret</li>
                  <li>Activa los permisos de &quot;meeting:write&quot;</li>
                </ol>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Account ID</label>
                  <Input
                    value={zoomForm.account_id}
                    onChange={(e) => setZoomForm({...zoomForm, account_id: e.target.value})}
                    placeholder="Tu Account ID de Zoom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Client ID</label>
                  <Input
                    value={zoomForm.client_id}
                    onChange={(e) => setZoomForm({...zoomForm, client_id: e.target.value})}
                    placeholder="Tu Client ID"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Client Secret</label>
                  <Input
                    type="password"
                    value={zoomForm.client_secret}
                    onChange={(e) => setZoomForm({...zoomForm, client_secret: e.target.value})}
                    placeholder="Tu Client Secret"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowZoomConfig(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleConfigureZoom} disabled={savingZoom}>
                  {savingZoom ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Create Meeting Modal */}
        {showCreateMeeting && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4">
              <h3 className="text-xl font-bold mb-4">Nueva Clase en Vivo</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Título de la clase *</label>
                  <Input
                    value={meetingForm.topic}
                    onChange={(e) => setMeetingForm({...meetingForm, topic: e.target.value})}
                    placeholder="Ej: Clase de repaso - Tema 5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Descripción</label>
                  <textarea
                    value={meetingForm.description}
                    onChange={(e) => setMeetingForm({...meetingForm, description: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    rows={2}
                    placeholder="Descripción opcional"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Fecha y hora *</label>
                    <Input
                      type="datetime-local"
                      value={meetingForm.start_time}
                      onChange={(e) => setMeetingForm({...meetingForm, start_time: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Duración (min)</label>
                    <Input
                      type="number"
                      value={meetingForm.duration_minutes}
                      onChange={(e) => setMeetingForm({...meetingForm, duration_minutes: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowCreateMeeting(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreateMeeting} disabled={savingZoom}>
                  {savingZoom ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Video className="w-4 h-4 mr-2" />}
                  Crear Clase
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* CRM Tab */}
        {activeTab === 'crm' && (
          <div className="space-y-6">
            {/* CRM Status Banner */}
            {crmConnection ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-800">CRM Conectado</p>
                    <p className="text-sm text-green-600">
                      {crmProviders.find(p => p.provider_id === crmConnection.crm_type)?.name || crmConnection.crm_type}
                      {' '} • Sincronización: {crmConnection.sync_frequency === 'realtime' ? 'Tiempo real' : crmConnection.sync_frequency === 'hourly' ? 'Cada hora' : 'Diaria'}
                    </p>
                  </div>
                </div>
                <Button variant="outline" onClick={disconnectCrm} className="text-red-600 border-red-200 hover:bg-red-50">
                  <Unlink className="w-4 h-4 mr-2" /> Desconectar
                </Button>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <p className="text-amber-800">
                    <strong>CRM no conectado.</strong> Conecta tu CRM para sincronizar automáticamente los datos de tus alumnos.
                  </p>
                </div>
              </div>
            )}

            {/* CRM Provider Selection */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-800 mb-4">Selecciona tu CRM</h3>
              
              {/* Popular CRMs */}
              <div className="mb-6">
                <p className="text-sm text-slate-500 mb-3">CRMs Populares</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {crmProviders.filter(p => p.is_popular).map(provider => (
                    <button
                      key={provider.provider_id}
                      onClick={() => setCrmForm(prev => ({ ...prev, crm_type: provider.provider_id }))}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        crmForm.crm_type === provider.provider_id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                          <Zap className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-medium text-slate-800">{provider.name}</span>
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2">{provider.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Other CRMs */}
              <div>
                <p className="text-sm text-slate-500 mb-3">Otros CRMs</p>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {crmProviders.filter(p => !p.is_popular).map(provider => (
                    <button
                      key={provider.provider_id}
                      onClick={() => setCrmForm(prev => ({ ...prev, crm_type: provider.provider_id }))}
                      className={`p-3 rounded-lg border transition-all text-left ${
                        crmForm.crm_type === provider.provider_id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {provider.logo_url ? (
                          <img src={provider.logo_url} alt={provider.name} className="w-6 h-6 rounded" />
                        ) : (
                          <div className="w-6 h-6 rounded bg-slate-200 flex items-center justify-center">
                            <Link2 className="w-3 h-3 text-slate-600" />
                          </div>
                        )}
                        <span className="text-sm font-medium text-slate-700">{provider.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* CRM Configuration Form */}
            {crmForm.crm_type && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4">
                  Configurar {crmProviders.find(p => p.provider_id === crmForm.crm_type)?.name}
                </h3>
                
                <div className="space-y-4">
                  {/* API Key - always show */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      API Key
                      <span className="text-slate-400 font-normal ml-2">Obtén tu API key desde tu panel de {crmProviders.find(p => p.provider_id === crmForm.crm_type)?.name}</span>
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type={showApiKey ? 'text' : 'password'}
                        value={crmForm.api_key}
                        onChange={(e) => setCrmForm(prev => ({ ...prev, api_key: e.target.value }))}
                        placeholder="Tu API key..."
                        className="flex-1 font-mono"
                      />
                      <Button variant="outline" onClick={() => setShowApiKey(!showApiKey)}>
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Location ID for HighLevel */}
                  {crmForm.crm_type === 'highlevel' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Location ID
                        <span className="text-slate-400 font-normal ml-2">Identificador de tu ubicación en HighLevel</span>
                      </label>
                      <Input
                        value={crmForm.location_id}
                        onChange={(e) => setCrmForm(prev => ({ ...prev, location_id: e.target.value }))}
                        placeholder="loc_xxxxxxxxxxxxx"
                      />
                    </div>
                  )}

                  {/* Domain for some CRMs */}
                  {['salesforce', 'zoho', 'freshsales', 'activecampaign'].includes(crmForm.crm_type) && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Dominio
                        <span className="text-slate-400 font-normal ml-2">Tu subdominio o dominio personalizado</span>
                      </label>
                      <Input
                        value={crmForm.domain}
                        onChange={(e) => setCrmForm(prev => ({ ...prev, domain: e.target.value }))}
                        placeholder="tu-empresa.crm.com"
                      />
                    </div>
                  )}

                  {/* API Secret for OAuth-based CRMs */}
                  {['salesforce', 'zoho', 'keap'].includes(crmForm.crm_type) && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        API Secret / Client Secret
                      </label>
                      <Input
                        type="password"
                        value={crmForm.api_secret}
                        onChange={(e) => setCrmForm(prev => ({ ...prev, api_secret: e.target.value }))}
                        placeholder="Tu client secret..."
                      />
                    </div>
                  )}

                  {/* Webhook URL for custom webhooks */}
                  {crmForm.crm_type === 'custom_webhook' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Webhook URL
                        <span className="text-slate-400 font-normal ml-2">URL donde enviaremos los datos</span>
                      </label>
                      <Input
                        value={crmForm.webhook_url}
                        onChange={(e) => setCrmForm(prev => ({ ...prev, webhook_url: e.target.value }))}
                        placeholder="https://tu-sistema.com/api/webhook"
                      />
                    </div>
                  )}

                  {/* Sync Options */}
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="font-medium text-slate-800 mb-3">Opciones de Sincronización</h4>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={crmForm.sync_students}
                          onChange={(e) => setCrmForm(prev => ({ ...prev, sync_students: e.target.checked }))}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                        />
                        <span className="text-slate-700">Sincronizar alumnos como contactos/leads</span>
                      </label>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={crmForm.sync_progress}
                          onChange={(e) => setCrmForm(prev => ({ ...prev, sync_progress: e.target.checked }))}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                        />
                        <span className="text-slate-700">Sincronizar progreso de estudio (campos personalizados)</span>
                      </label>
                    </div>
                    
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Frecuencia de Sincronización</label>
                      <select
                        value={crmForm.sync_frequency}
                        onChange={(e) => setCrmForm(prev => ({ ...prev, sync_frequency: e.target.value }))}
                        className="w-full p-2 border border-slate-300 rounded-lg"
                      >
                        <option value="realtime">Tiempo real</option>
                        <option value="hourly">Cada hora</option>
                        <option value="daily">Diaria</option>
                      </select>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="pt-4">
                    <Button
                      onClick={saveCrmConnection}
                      disabled={savingCrm}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      {savingCrm ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Guardar Conexión CRM</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PWA Tab */}
        {activeTab === 'pwa' && (
          <PWABrandingConfig />
        )}

        {/* Emails Tab */}
        {activeTab === 'emails' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                <Mail className="w-5 h-5 text-indigo-600" />
                Configuración de Emails
              </h2>
              <p className="text-slate-600 mb-6">
                Configura cómo se envían los emails a tus alumnos (bienvenida, recordatorios, etc.)
              </p>

              {/* Mode Selector */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <button
                  onClick={() => setEmailConfig({...emailConfig, mode: 'tuplazaia'})}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    emailConfig.mode === 'tuplazaia'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">TuPlazaIA envía por ti</p>
                      <p className="text-xs text-slate-500">Recomendado - Sin configuración</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    Nosotros nos encargamos de enviar los emails a tus alumnos. Los emails se enviarán desde noreply@tuplazaia.com con tu nombre de academia.
                  </p>
                </button>

                <button
                  onClick={() => setEmailConfig({...emailConfig, mode: 'custom'})}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    emailConfig.mode === 'custom'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Settings className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">Mi propio Amazon SES</p>
                      <p className="text-xs text-slate-500">Avanzado - Requiere AWS</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    Los emails se enviarán desde tu propio dominio usando Amazon SES. Necesitas configurar y verificar tu cuenta de AWS.
                  </p>
                </button>
              </div>

              {/* TuPlazaIA Mode */}
              {emailConfig.mode === 'tuplazaia' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800">¡Todo listo!</p>
                      <p className="text-sm text-green-700 mt-1">
                        Los emails se enviarán automáticamente desde <strong>noreply@tuplazaia.com</strong> con el nombre de tu academia: <strong>{institution?.name || 'Tu Academia'}</strong>
                      </p>
                      <p className="text-xs text-green-600 mt-2">
                        Incluye: bienvenida a nuevos alumnos, recordatorios de estudio, recuperación de contraseña.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Custom SES Mode */}
              {emailConfig.mode === 'custom' && (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">Configuración avanzada</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Necesitas una cuenta de AWS con Amazon SES configurado. Tu dominio/email debe estar verificado en SES.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">AWS Access Key ID</label>
                      <Input
                        type="text"
                        value={emailConfig.aws_access_key_id}
                        onChange={(e) => setEmailConfig({...emailConfig, aws_access_key_id: e.target.value})}
                        placeholder="AKIA..."
                        className="font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">AWS Secret Access Key</label>
                      <div className="flex gap-2">
                        <Input
                          type={showAwsSecret ? 'text' : 'password'}
                          value={emailConfig.aws_secret_access_key}
                          onChange={(e) => setEmailConfig({...emailConfig, aws_secret_access_key: e.target.value})}
                          placeholder="Tu secret key..."
                          className="font-mono flex-1"
                        />
                        <Button variant="outline" onClick={() => setShowAwsSecret(!showAwsSecret)}>
                          {showAwsSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Región AWS</label>
                      <select
                        value={emailConfig.aws_region}
                        onChange={(e) => setEmailConfig({...emailConfig, aws_region: e.target.value})}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value="eu-west-1">EU (Ireland) - eu-west-1</option>
                        <option value="eu-west-2">EU (London) - eu-west-2</option>
                        <option value="eu-central-1">EU (Frankfurt) - eu-central-1</option>
                        <option value="us-east-1">US East (N. Virginia) - us-east-1</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email Remitente (verificado)</label>
                      <Input
                        type="email"
                        value={emailConfig.ses_from_email}
                        onChange={(e) => setEmailConfig({...emailConfig, ses_from_email: e.target.value})}
                        placeholder="noreply@tuacademia.com"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Nombre del remitente</label>
                      <Input
                        type="text"
                        value={emailConfig.ses_from_name}
                        onChange={(e) => setEmailConfig({...emailConfig, ses_from_name: e.target.value})}
                        placeholder="Tu Academia de Oposiciones"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={async () => {
                      setSavingEmailConfig(true);
                      try {
                        const token = localStorage.getItem('session_token');
                        await axios.put(`${API}/institutional/email-config`, emailConfig, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success('Configuración de email guardada');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Error al guardar');
                      } finally {
                        setSavingEmailConfig(false);
                      }
                    }}
                    disabled={savingEmailConfig}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    {savingEmailConfig ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Guardar Configuración SES</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <>
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-2">
              <Users className="w-4 h-4" />
              <span className="text-xs">Alumnos</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{stats?.total_students || students.length}</p>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-2">
              <UserCheck className="w-4 h-4" />
              <span className="text-xs">Activos hoy</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{stats?.active_today || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-2">
              <Clock className="w-4 h-4" />
              <span className="text-xs">Horas totales</span>
            </div>
            <p className="text-2xl font-bold text-indigo-600">{stats?.total_study_hours?.toFixed(0) || 0}h</p>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-2">
              <Target className="w-4 h-4" />
              <span className="text-xs">Media aciertos</span>
            </div>
            <p className="text-2xl font-bold text-slate-800">{stats?.avg_success_rate?.toFixed(0) || 0}%</p>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-red-500 mb-2">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs">Riesgo suspenso</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{stats?.at_risk_fail || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-amber-500 mb-2">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs">Riesgo abandono</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{stats?.at_risk_dropout || 0}</p>
          </div>

          {/* Wellbeing Card */}
          <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl border border-pink-200 p-4">
            <div className="flex items-center gap-2 text-pink-500 mb-2">
              <Heart className="w-4 h-4" />
              <span className="text-xs">Bienestar</span>
            </div>
            <p className="text-2xl font-bold text-pink-600">{stats?.wellbeing_avg?.toFixed(1) || 'N/A'}</p>
            <p className="text-xs text-slate-500">Media semanal</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar alumno..."
                  className="pl-10"
                />
              </div>
            </div>
            <select
              value={filterRisk}
              onChange={(e) => setFilterRisk(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg"
            >
              <option value="all">Todos los alumnos</option>
              <option value="high_risk">🔴 Alto riesgo</option>
              <option value="at_risk">🟡 En riesgo</option>
              <option value="on_track">🟢 En buen camino</option>
            </select>
            <div className="flex gap-2">
              <Button onClick={() => setShowAddStudentModal(true)}>
                <Plus className="w-4 h-4 mr-2" /> Añadir Alumno
              </Button>
              <Button variant="outline" onClick={() => setShowImportModal(true)}>
                <Upload className="w-4 h-4 mr-2" /> Importar CSV
              </Button>
              <Button variant="outline" onClick={exportStudentsCsv}>
                <Download className="w-4 h-4 mr-2" /> Exportar
              </Button>
            </div>
          </div>
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Alumno</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Racha</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Horas</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Exámenes</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">% Acierto</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Prob. Aprobar</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Riesgo</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Última actividad</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map(student => (
                  <tr 
                    key={student.user_id} 
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => {
                      setSelectedStudent(student);
                      loadStudentDetails(student.user_id);
                    }}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-600 font-medium">
                            {student.name?.charAt(0) || student.email?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{student.name || 'Sin nombre'}</p>
                          <p className="text-sm text-slate-500">{student.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <Flame className={`w-4 h-4 ${student.streak > 0 ? 'text-orange-500' : 'text-slate-300'}`} />
                        <span className="font-medium">{student.streak || 0} días</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="font-medium">{(student.study_hours || 0).toFixed(1)}h</span>
                    </td>
                    <td className="p-4">
                      <span className="font-medium">{student.exams_completed || 0}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-indigo-500 rounded-full"
                            style={{ width: `${student.success_rate || 0}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">{(student.success_rate || 0).toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`font-medium ${
                        (student.pass_probability || 0) >= 70 ? 'text-green-600' :
                        (student.pass_probability || 0) >= 40 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {(student.pass_probability || 0).toFixed(0)}%
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getRiskColor(student.risk_score || 0)}`}>
                        {getRiskLabel(student.risk_score || 0)}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-500">
                      {student.last_activity 
                        ? new Date(student.last_activity).toLocaleDateString()
                        : 'Nunca'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredStudents.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              {students.length === 0 ? (
                <>
                  <p className="text-slate-500 font-medium">Aún no hay alumnos en tu institución</p>
                  <p className="text-slate-400 text-sm mt-1">Invita alumnos o importa desde un archivo CSV</p>
                </>
              ) : (
                <>
                  <p className="text-slate-500">No se encontraron alumnos con estos filtros</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={() => { setSearchTerm(''); setFilterRisk('all'); }}
                  >
                    Limpiar filtros
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Student Detail Modal */}
        {selectedStudent && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedStudent(null)}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-2xl text-indigo-600 font-bold">
                      {selectedStudent.name?.charAt(0) || '?'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800">{selectedStudent.name || 'Sin nombre'}</h3>
                    <p className="text-slate-500">{selectedStudent.email}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <Flame className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{selectedStudent.streak || 0}</p>
                    <p className="text-sm text-slate-500">Días de racha</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <Clock className="w-6 h-6 text-indigo-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{(selectedStudent.study_hours || 0).toFixed(1)}h</p>
                    <p className="text-sm text-slate-500">Horas de estudio</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <Trophy className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{selectedStudent.exams_completed || 0}</p>
                    <p className="text-sm text-slate-500">Exámenes</p>
                  </div>
                </div>

                {/* Risk Analysis */}
                <div>
                  <h4 className="font-medium text-slate-800 mb-3">Análisis de Riesgo</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Probabilidad de aprobar</span>
                      <span className={`font-semibold ${
                        (selectedStudent.pass_probability || 0) >= 70 ? 'text-green-600' :
                        (selectedStudent.pass_probability || 0) >= 40 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {(selectedStudent.pass_probability || 0).toFixed(0)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Riesgo de suspenso</span>
                      <span className={`font-semibold ${
                        (selectedStudent.fail_risk || 0) > 50 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {(selectedStudent.fail_risk || 0).toFixed(0)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Riesgo de abandono</span>
                      <span className={`font-semibold ${
                        (selectedStudent.dropout_risk || 0) > 50 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {(selectedStudent.dropout_risk || 0).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                {studentDetails?.recent_exams && (
                  <div>
                    <h4 className="font-medium text-slate-800 mb-3">Últimos exámenes</h4>
                    <div className="space-y-2">
                      {studentDetails.recent_exams.slice(0, 5).map((exam, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium">{exam.exam_type}</p>
                            <p className="text-xs text-slate-500">
                              {exam.completed_at ? new Date(exam.completed_at).toLocaleDateString() : ''}
                            </p>
                          </div>
                          <span className={`font-medium ${
                            exam.score >= 50 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {exam.score?.toFixed(1) || 0}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-slate-200">
                <Button variant="outline" className="w-full" onClick={() => setSelectedStudent(null)}>
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        )}
          </>
        )}

        {/* Import CSV Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Importar Alumnos desde CSV</h3>
                <button onClick={() => { setShowImportModal(false); setImportResult(null); }} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Formato del archivo CSV:</p>
                  <code className="text-xs bg-slate-200 px-2 py-1 rounded block">
                    email,name,profession_id,region
                  </code>
                  <p className="text-xs text-slate-500 mt-2">
                    <strong>email</strong> y <strong>name</strong> son obligatorios. profession_id y region son opcionales.
                  </p>
                </div>

                <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-indigo-400 transition-colors">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleImportCsv}
                    className="hidden"
                    id="csv-upload"
                    disabled={importingCsv}
                  />
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    {importingCsv ? (
                      <Loader2 className="w-10 h-10 mx-auto mb-3 text-indigo-600 animate-spin" />
                    ) : (
                      <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                    )}
                    <p className="font-medium text-slate-700">
                      {importingCsv ? 'Importando...' : 'Selecciona un archivo CSV'}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      o arrastra y suelta aquí
                    </p>
                  </label>
                </div>

                {importResult && (
                  <div className={`rounded-lg p-4 ${importResult.added > 0 ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                    <p className="font-medium mb-2">
                      {importResult.added > 0 ? '✅ ' : '⚠️ '}
                      Resultado de la importación:
                    </p>
                    <ul className="text-sm space-y-1">
                      <li>Alumnos añadidos: <strong>{importResult.added || 0}</strong></li>
                      <li>Alumnos actualizados: <strong>{importResult.updated || 0}</strong></li>
                      {importResult.failed?.length > 0 && (
                        <li className="text-red-600">
                          Errores: <strong>{importResult.failed.length}</strong>
                          <ul className="ml-4 mt-1">
                            {importResult.failed.slice(0, 3).map((f, i) => (
                              <li key={i} className="text-xs">{f.email}: {f.reason}</li>
                            ))}
                            {importResult.failed.length > 3 && (
                              <li className="text-xs">...y {importResult.failed.length - 3} más</li>
                            )}
                          </ul>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <Button variant="outline" onClick={() => { setShowImportModal(false); setImportResult(null); }}>
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add Student Manual Modal */}
        {showAddStudentModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold">Añadir Alumno</h3>
                <button onClick={() => setShowAddStudentModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nombre completo *
                  </label>
                  <Input
                    placeholder="Juan García López"
                    value={newStudent.name}
                    onChange={(e) => setNewStudent({...newStudent, name: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email *
                  </label>
                  <Input
                    type="email"
                    placeholder="alumno@email.com"
                    value={newStudent.email}
                    onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                  />
                </div>

                <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                  <p className="font-medium mb-1">📧 Email de bienvenida</p>
                  <p>El alumno recibirá un email con sus credenciales de acceso (contraseña generada automáticamente).</p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowAddStudentModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleAddStudent} disabled={addingStudent}>
                  {addingStudent ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Añadiendo...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Añadir Alumno
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstitutionalDashboard;
